import type { components } from "umbraco"
import { getMediaUrl, getLinkUrl, getLinkType, getBlockList } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { getNonUmbracoUrl } from "utils/getNonUmbracoUrl"

type ApiBlockListModel = components["schemas"]["ApiBlockListModel"]
type ContactChannelItemProperties =
  components["schemas"]["ContactChannelItemPropertiesModel"]

/** Anything that exposes a `channels` Block List — the modal and the block both do. */
export interface ChannelsHost {
  channels?: ApiBlockListModel
}

export type ChannelType = "whatsapp" | "phone" | "externalLink"

export interface ResolvedChannel {
  key: string
  label: string
  channelType: ChannelType
  /** CMS-provided icon, when the editor uploaded one. Falls back to a predefined glyph. */
  iconUrl: string | null
  href: string
  external: boolean
}

/**
 * Build the href for a channel based on its channelType.
 * Returns null when the channel has no usable target.
 */
function buildChannelHref(
  props: ContactChannelItemProperties,
): { href: string; external: boolean } | null {
  const channelType = props.channelType ?? ""

  if (channelType === "whatsapp") {
    const digits = (props.phoneNumber ?? "").replace(/\D/g, "")
    if (!digits) return null
    const text = props.whatsappMessage
      ? `?text=${encodeURIComponent(props.whatsappMessage)}`
      : ""
    return { href: `https://wa.me/${digits}${text}`, external: true }
  }

  if (channelType === "phone") {
    const phone = (props.phoneNumber ?? "").trim()
    if (!phone) return null
    return { href: `tel:${phone}`, external: false }
  }

  // externalLink (and any unknown type) — use the link picker URL. Internal
  // content links carry the Umbraco tree path (…/pages/contact); strip the
  // /pages segment so navigation hits the public route and let the locale-aware
  // <Link> add the locale (EGDE-282). True external/media links pass through
  // unchanged and open in a new tab.
  const url = getLinkUrl(props.link)
  if (!url) return null
  const isInternal =
    getLinkType(props.link) === "Content" ||
    (!/^https?:\/\//.test(url) && url.includes("pages"))
  if (isInternal) {
    return { href: getNonUmbracoUrl(url), external: false }
  }
  return { href: url, external: true }
}

function normalizeChannelType(raw: string | null | undefined): ChannelType {
  return raw === "whatsapp" || raw === "phone" ? raw : "externalLink"
}

/**
 * Resolve a `channels` Block List into render-ready cards. A channel is included
 * only when it is enabled and has both a label and a usable target. This is the
 * single source of truth for "usable channel", shared by the global Contact modal
 * and the standalone Contact Channels block.
 */
export function resolveChannels(
  data: ChannelsHost | null,
): ResolvedChannel[] {
  if (!data) return []

  return getBlockList(
    { properties: { channels: data.channels } } as never,
    "channels",
  )
    .map((block, index): ResolvedChannel | null => {
      const props = block.content.properties as ContactChannelItemProperties
      if (props.enabled !== true) return null

      // EGDE-367: a channel only needs a usable target — an empty label must NOT
      // drop the channel (which previously hid its icon entirely). Render the
      // icon with no caption; the link still carries an accessible name (the
      // label when present, else the channel type — see ContactChannels/Modal).
      const label = props.label?.trim() ?? ""

      const resolved = buildChannelHref(props)
      if (!resolved) return null

      const iconRel = getMediaUrl(props.icon)
      const iconUrl = iconRel ? getUmbracoMediaUrl(iconRel) : null

      return {
        key: block.content.id ?? `${index}`,
        label,
        channelType: normalizeChannelType(props.channelType),
        iconUrl,
        href: resolved.href,
        external: resolved.external,
      }
    })
    .filter((c): c is ResolvedChannel => c !== null)
}
